import type { PolicyEndorsementViewModel } from '@/api/types/PolicyEndorsementViewModel';
import type {
  EndorsementTypeLookupOption,
  UpdatePolicyEndorsementRequest,
  EndorsementDescriptorTypeLookupOption,
} from 'src/api/services/policy.service';

import { useParams } from 'react-router';
import { varAlpha } from 'minimal-shared/utils';
import { memo, useRef, useMemo, useState, useEffect, forwardRef, useImperativeHandle } from 'react';

import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Card,
  Table,
  Collapse,
  TableRow,
  useTheme,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  Typography,
  IconButton,
  CardContent,
  Autocomplete,
  TableContainer,
} from '@mui/material';

import { useEndorsementsByPolicyId } from 'src/api/hooks/usePolicyDetailsQueries';
import {
  useEndorsementTypeLookups,
  useEndorsementDescriptorTypeLookups,
} from 'src/api/hooks/usePolicyQueries';

import { TableNoData } from 'src/components/table';

import { formatDate } from '../../shared/utils/policyUtils';
import { useHeaderWidth } from '../../shared/hooks/useHeaderWidth';

type DraftWc000308 = { endorsementWc000308Id?: number; descriptorCode: string; excludedName: string };
type DraftWc000310 = { endorsementWc000310Id?: number; descriptorCode: string; includedName: string };
type EndorsementChildRow = {
  id?: number;
  name: string;
  descriptorCode: string;
  kind: 'excluded' | 'included';
};
export type DraftEndorsement = {
  endorsementId: number;
  endorsementTypeId: number;
  endorsementCode: string;
  endorsementDescription: string;
  bureauVersionNumber: string;
  policyChangeEffectiveDate: string;
  policyChangeExpirationDate: string;
  createdDate?: string;
  wc000308s: DraftWc000308[];
  wc000310s: DraftWc000310[];
};

export type EndorsementsHandle = {
  validateForSave: () => boolean;
  hasPendingChanges: () => boolean;
  getChangedFieldSummaries: () => string[];
  getEndorsementsPayload: () => UpdatePolicyEndorsementRequest[];
  commitEdit: () => void;
  cancelEdit: () => void;
};

// Changed-values-only representation of endorsement edits, for cross-tab-switch persistence without duplicating unedited read data.
export type EndorsementsDraftDelta = Record<number, Partial<Omit<DraftEndorsement, 'endorsementId'>>>;

const endorsementScalarFields = [
  'endorsementTypeId',
  'endorsementCode',
  'endorsementDescription',
  'bureauVersionNumber',
  'policyChangeEffectiveDate',
  'policyChangeExpirationDate',
] as const;

export const getEndorsementsDraftDelta = (
  snapshotRows: DraftEndorsement[],
  draftRows: DraftEndorsement[]
): EndorsementsDraftDelta => {
  const delta: EndorsementsDraftDelta = {};
  const previousById = new Map(snapshotRows.map((row) => [row.endorsementId, row]));

  draftRows.forEach((row) => {
    const previous = previousById.get(row.endorsementId);
    if (!previous) {
      return;
    }

    const patch: Partial<Omit<DraftEndorsement, 'endorsementId'>> = {};
    endorsementScalarFields.forEach((field) => {
      if (row[field] !== previous[field]) {
        (patch as Record<string, unknown>)[field] = row[field];
      }
    });
    if (JSON.stringify(row.wc000308s) !== JSON.stringify(previous.wc000308s)) patch.wc000308s = row.wc000308s;
    if (JSON.stringify(row.wc000310s) !== JSON.stringify(previous.wc000310s)) patch.wc000310s = row.wc000310s;

    if (Object.keys(patch).length > 0) delta[row.endorsementId] = patch;
  });

  return delta;
};

export const applyEndorsementsDraftDelta = (
  freshRows: DraftEndorsement[],
  delta: EndorsementsDraftDelta | null | undefined
): DraftEndorsement[] => {
  if (!delta) {
    return freshRows;
  }

  return freshRows.map((row) => (delta[row.endorsementId] ? { ...row, ...delta[row.endorsementId] } : row));
};

// Standalone validation/summary helpers, callable without the component mounted (e.g. validating/saving
// a cached-but-unmounted tab by reconstructing its draft from view data + cached delta).
export const validateEndorsementRows = (
  rows: DraftEndorsement[],
  typeOptions: EndorsementTypeLookupOption[],
  policyEffectiveDate?: string | null
): Record<string, string> => {
  const nextErrors: Record<string, string> = {};
  rows.forEach((row) => {
    const endorsementTypeError = getEndorsementTypeSelectionError(row.endorsementTypeId, typeOptions, policyEffectiveDate);
    if (endorsementTypeError) nextErrors[`${row.endorsementId}.endorsementTypeId`] = endorsementTypeError;
    if (!row.policyChangeEffectiveDate || !row.policyChangeExpirationDate || row.policyChangeEffectiveDate > row.policyChangeExpirationDate) {
      nextErrors[`${row.endorsementId}.effectiveDate`] = 'Enter a valid effective and expiration date range.';
      nextErrors[`${row.endorsementId}.expirationDate`] = 'Enter a valid effective and expiration date range.';
    }
    row.wc000308s.forEach((item) => {
      if (!item.excludedName.trim()) nextErrors[`${row.endorsementId}.wc000308s.${item.endorsementWc000308Id}.excludedName`] = 'Enter an excluded name.';
      if (!item.descriptorCode) nextErrors[`${row.endorsementId}.wc000308s.${item.endorsementWc000308Id}.descriptorCode`] = 'Select a descriptor.';
    });
    row.wc000310s.forEach((item) => {
      if (!item.includedName.trim()) nextErrors[`${row.endorsementId}.wc000310s.${item.endorsementWc000310Id}.includedName`] = 'Enter an included name.';
      if (!item.descriptorCode) nextErrors[`${row.endorsementId}.wc000310s.${item.endorsementWc000310Id}.descriptorCode`] = 'Select a descriptor.';
    });
  });
  return nextErrors;
};

export const getEndorsementsChangedFieldSummaries = (
  snapshotRows: DraftEndorsement[] | null,
  draftRows: DraftEndorsement[],
  typeOptions: EndorsementTypeLookupOption[],
  descriptorOptions: EndorsementDescriptorTypeLookupOption[]
): string[] => {
  if (!snapshotRows) return [];
  const summaries: string[] = [];
  draftRows.forEach((row, index) => {
    const previous = snapshotRows[index];
    if (!previous) return;
    summaries.push(...getEndorsementScalarSummaries(previous, row, typeOptions));
    summaries.push(...getExcludedChildSummaries(previous, row, descriptorOptions));
    summaries.push(...getIncludedChildSummaries(previous, row, descriptorOptions));
  });
  return summaries;
};

const toDateInput = (value?: string | null) => value?.slice(0, 10) ?? '';
const toRequestDate = (value: string) => value ? `${value.slice(5, 7)}/${value.slice(8, 10)}/${value.slice(0, 4)}` : undefined;
const valueForSummary = (value?: string) => value || '[blank]';

const getEndorsementScalarSummaries = (
  previous: DraftEndorsement,
  current: DraftEndorsement,
  typeOptions: EndorsementTypeLookupOption[]
): string[] => {
  const summaries: string[] = [];
  if (previous.endorsementTypeId !== current.endorsementTypeId) {
    summaries.push(`Endorsement changed from ${endorsementLabel(previous, typeOptions)} to ${endorsementLabel(current, typeOptions)}`);
  }
  if (previous.policyChangeEffectiveDate !== current.policyChangeEffectiveDate) {
    summaries.push(`Effective date changed from ${valueForSummary(previous.policyChangeEffectiveDate)} to ${valueForSummary(current.policyChangeEffectiveDate)}`);
  }
  if (previous.policyChangeExpirationDate !== current.policyChangeExpirationDate) {
    summaries.push(`Expiration date changed from ${valueForSummary(previous.policyChangeExpirationDate)} to ${valueForSummary(current.policyChangeExpirationDate)}`);
  }
  return summaries;
};

const getExcludedChildSummaries = (
  previous: DraftEndorsement,
  current: DraftEndorsement,
  descriptorOptions: EndorsementDescriptorTypeLookupOption[]
): string[] => {
  const summaries: string[] = [];
  current.wc000308s.forEach((item) => {
    const old = previous.wc000308s.find((value) => value.endorsementWc000308Id === item.endorsementWc000308Id);
    if (old?.excludedName !== item.excludedName) {
      summaries.push(`Excluded name changed from ${valueForSummary(old?.excludedName)} to ${valueForSummary(item.excludedName)}`);
    }
    if (old?.descriptorCode !== item.descriptorCode) {
      summaries.push(`Excluded title changed from ${valueForSummary(descriptorLabel(old?.descriptorCode ?? '', descriptorOptions))} to ${valueForSummary(descriptorLabel(item.descriptorCode, descriptorOptions))}`);
    }
  });
  return summaries;
};

const getIncludedChildSummaries = (
  previous: DraftEndorsement,
  current: DraftEndorsement,
  descriptorOptions: EndorsementDescriptorTypeLookupOption[]
): string[] => {
  const summaries: string[] = [];
  current.wc000310s.forEach((item) => {
    const old = previous.wc000310s.find((value) => value.endorsementWc000310Id === item.endorsementWc000310Id);
    if (old?.includedName !== item.includedName) {
      summaries.push(`Included name changed from ${valueForSummary(old?.includedName)} to ${valueForSummary(item.includedName)}`);
    }
    if (old?.descriptorCode !== item.descriptorCode) {
      summaries.push(`Included title changed from ${valueForSummary(descriptorLabel(old?.descriptorCode ?? '', descriptorOptions))} to ${valueForSummary(descriptorLabel(item.descriptorCode, descriptorOptions))}`);
    }
  });
  return summaries;
};

export const toDraft = (item: PolicyEndorsementViewModel, index: number): DraftEndorsement => ({
  endorsementId: item.endorsementId ?? index,
  endorsementTypeId: item.endorsementCodeNavigation?.endorsementTypeId ?? 0,
  endorsementCode: item.endorsementCode ?? item.endorsementCodeNavigation?.endorsementCode ?? '',
  endorsementDescription: item.endorsementCodeNavigation?.endorsementDescription ?? '',
  bureauVersionNumber: item.bureauVersionNumber ?? '',
  policyChangeEffectiveDate: toDateInput(item.policyChangeEffectiveDate),
  policyChangeExpirationDate: toDateInput(item.policyChangeExpirationDate),
  createdDate: item.createdDate,
  wc000308s: (item.endorsementWc000308s ?? []).map((child) => ({
    endorsementWc000308Id: child.endorsementWc000308Id ?? undefined,
    descriptorCode: child.descriptorCode ?? '',
    excludedName: child.excludedName ?? '',
  })),
  wc000310s: (item.endorsementWc000310s ?? []).map((child) => ({
    endorsementWc000310Id: child.endorsementWc000310Id ?? undefined,
    descriptorCode: child.descriptorCode ?? '',
    includedName: child.includedName ?? '',
  })),
});

// These endorsement type codes are the only ones backed by the Wc000308/Wc000310 child tables.
const WC000308_ENDORSEMENT_CODE = 'WC000308';
const WC000310_ENDORSEMENT_CODE = 'WC000310';

const supportsWc000308 = (endorsementCode: string) => endorsementCode.trim().toUpperCase() === WC000308_ENDORSEMENT_CODE;
const supportsWc000310 = (endorsementCode: string) => endorsementCode.trim().toUpperCase() === WC000310_ENDORSEMENT_CODE;

const endorsementLabel = (row: DraftEndorsement, options: EndorsementTypeLookupOption[]) => {
  const option = options.find((item) => item.endorsementTypeId === row.endorsementTypeId);
  return option ? `${option.endorsementCode} - ${option.endorsementDescription}` : row.endorsementDescription || row.endorsementCode;
};

const endorsementViewLabel = (row: DraftEndorsement, options: EndorsementTypeLookupOption[]) => {
  const option = options.find((item) => item.endorsementTypeId === row.endorsementTypeId);
  return option ? `${option.endorsementDescription} (${option.endorsementCode})` : row.endorsementDescription || row.endorsementCode;
};

const endorsementViewText = (row: DraftEndorsement, options: EndorsementTypeLookupOption[]) => {
  const label = endorsementViewLabel(row, options);
  const version = row.bureauVersionNumber.trim();
  return version ? `${label} (${version})` : label;
};

const descriptorLabel = (code: string, options: EndorsementDescriptorTypeLookupOption[]) =>
  options.find((item) => item.descriptorCode === code)?.descriptorDescription ?? code;

const inRange = (value: string, start?: string | null, end?: string | null) => {
  if (!value || !start) return false;
  const current = new Date(value).valueOf();
  const first = new Date(start).valueOf();
  const last = end ? new Date(end).valueOf() : Number.POSITIVE_INFINITY;
  return !Number.isNaN(current) && !Number.isNaN(first) && current >= first && current <= last;
};

const getEndorsementTypeSelectionError = (
  endorsementTypeId: number,
  options: EndorsementTypeLookupOption[],
  policyEffectiveDate?: string | null
): string | undefined => {
  const type = options.find((item) => item.endorsementTypeId === endorsementTypeId);
  if (!type) return 'Select a valid endorsement type.';
  if (!inRange(policyEffectiveDate?.slice(0, 10) ?? '', type.endorsementVersionEffectiveDate, type.endorsementVersionExpirationDate)) {
    return 'The selected endorsement type is not effective for the policy effective date.';
  }

  return undefined;
};

export const toPayload = (rows: DraftEndorsement[]): UpdatePolicyEndorsementRequest[] => rows.map((row) => ({
  endorsementId: row.endorsementId,
  endorsementTypeId: row.endorsementTypeId,
  policyChangeEffectiveDate: toRequestDate(row.policyChangeEffectiveDate),
  policyChangeExpirationDate: toRequestDate(row.policyChangeExpirationDate),
  wc000308s: row.wc000308s,
  wc000310s: row.wc000310s,
}));

function DescriptorField({ value, options, error, onChange }: Readonly<{ value: string; options: EndorsementDescriptorTypeLookupOption[]; error?: string; onChange: (value: string) => void }>) {
  const selected = options.find((item) => item.descriptorCode === value) ?? null;
  return (
    <Autocomplete
      size="small"
      options={options}
      value={selected}
      getOptionLabel={(item) => item.descriptorDescription}
      isOptionEqualToValue={(left, right) => left.descriptorCode === right.descriptorCode}
      onChange={(_, option) => onChange(option?.descriptorCode ?? '')}
      renderInput={(params) => <TextField {...params} error={Boolean(error)} helperText={error} />}
    />
  );
}

function EndorsementRow({ row, rowIndex, isEditMode, typeOptions, descriptorOptions, errors, update, onEndorsementTypeChange }: Readonly<{
  row: DraftEndorsement;
  rowIndex: number;
  isEditMode: boolean;
  typeOptions: EndorsementTypeLookupOption[];
  descriptorOptions: EndorsementDescriptorTypeLookupOption[];
  errors: Record<string, string>;
  update: (id: number, value: Partial<DraftEndorsement>) => void;
  onEndorsementTypeChange: (id: number, option: EndorsementTypeLookupOption | null) => void;
}>) {
  const [expanded, setExpanded] = useState(isEditMode);
  const theme = useTheme();
  useEffect(() => {
    if (isEditMode) {
      setExpanded(true);
    }
  }, [isEditMode]);
  const selectedType = typeOptions.find((item) => item.endorsementTypeId === row.endorsementTypeId) ?? null;
  const childrenExist = row.wc000308s.length > 0 || row.wc000310s.length > 0;
  const childRows = [
    ...row.wc000308s.map((item) => ({ id: item.endorsementWc000308Id, name: item.excludedName, descriptorCode: item.descriptorCode, kind: 'excluded' as const })),
    ...row.wc000310s.map((item) => ({ id: item.endorsementWc000310Id, name: item.includedName, descriptorCode: item.descriptorCode, kind: 'included' as const })),
  ];
  const updateChild = (child: EndorsementChildRow, value: Partial<{ name: string; descriptorCode: string }>) => {
    if (child.kind === 'excluded') {
      update(row.endorsementId, {
        wc000308s: row.wc000308s.map((item) => item.endorsementWc000308Id === child.id
          ? { ...item, excludedName: value.name ?? item.excludedName, descriptorCode: value.descriptorCode ?? item.descriptorCode }
          : item),
      });
    } else {
      update(row.endorsementId, {
        wc000310s: row.wc000310s.map((item) => item.endorsementWc000310Id === child.id
          ? { ...item, includedName: value.name ?? item.includedName, descriptorCode: value.descriptorCode ?? item.descriptorCode }
          : item),
      });
    }
  };
  const renderChildRow = (child: EndorsementChildRow) => {
    const prefix = `${row.endorsementId}.wc000${child.kind === 'excluded' ? '308' : '310'}s.${child.id}`;
    const nameErrorKey = `${prefix}.${child.kind === 'excluded' ? 'excludedName' : 'includedName'}`;
    return (
      <TableRow key={`${child.kind}-${child.id}`}>
        <TableCell data-testid={`row-${rowIndex}-endorsement-name-${child.id}`}>
          {isEditMode ? (
            <TextField
              size="small"
              fullWidth
              value={child.name}
              onChange={(event) => updateChild(child, { name: event.target.value })}
              error={Boolean(errors[nameErrorKey])}
              helperText={errors[nameErrorKey]}
            />
          ) : child.name || 'N/A'}
        </TableCell>
        <TableCell data-testid={`row-${rowIndex}-endorsement-title-${child.id}`}>
          {isEditMode ? (
            <DescriptorField
              value={child.descriptorCode}
              options={descriptorOptions}
              error={errors[`${prefix}.descriptorCode`]}
              onChange={(descriptorCode) => updateChild(child, { descriptorCode })}
            />
          ) : descriptorLabel(child.descriptorCode, descriptorOptions) || 'N/A'}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          {childrenExist && <IconButton aria-label="expand row" onClick={() => setExpanded((current) => !current)}>{expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>}
        </TableCell>
        <TableCell data-testid={`endorsement-${rowIndex}`}>
          {isEditMode ? (
            <Autocomplete
              sx={{ minWidth: 320 }}
              size="small"
              options={typeOptions}
              value={selectedType}
              getOptionLabel={(item) => `${item.endorsementCode} - ${item.endorsementDescription}`}
              isOptionEqualToValue={(left, right) => left.endorsementTypeId === right.endorsementTypeId}
              onChange={(_, option) => onEndorsementTypeChange(row.endorsementId, option)}
              renderOption={(props, option) => (
                <li {...props} key={option.endorsementTypeId}>
                  {`${option.endorsementCode} - ${option.endorsementDescription}`}
                </li>
              )}
              renderInput={(params) => <TextField {...params} error={Boolean(errors[`${row.endorsementId}.endorsementTypeId`])} helperText={errors[`${row.endorsementId}.endorsementTypeId`]} />}
            />
          ) : endorsementViewText(row, typeOptions)}
        </TableCell>
        <TableCell data-testid={`effective-date-${rowIndex}`}>
          {isEditMode ? <TextField size="small" type="date" value={row.policyChangeEffectiveDate} onChange={(event) => update(row.endorsementId, { policyChangeEffectiveDate: event.target.value })} error={Boolean(errors[`${row.endorsementId}.effectiveDate`])} helperText={errors[`${row.endorsementId}.effectiveDate`]} /> : formatDate(row.policyChangeEffectiveDate)}
        </TableCell>
        <TableCell data-testid={`expiration-date-${rowIndex}`}>
          {isEditMode ? <TextField size="small" type="date" value={row.policyChangeExpirationDate} onChange={(event) => update(row.endorsementId, { policyChangeExpirationDate: event.target.value })} error={Boolean(errors[`${row.endorsementId}.expirationDate`])} helperText={errors[`${row.endorsementId}.expirationDate`]} /> : formatDate(row.policyChangeExpirationDate)}
        </TableCell>
        <TableCell data-testid={`received-${rowIndex}`}>{formatDate(row.createdDate)}</TableCell>
        <TableCell />
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ paddingBottom: 0, paddingTop: 0, backgroundColor: expanded ? varAlpha(theme.vars?.palette.grey['500Channel'] ?? '158 158 158', 0.4) : 'inherit' }}>
          <Collapse in={childrenExist && expanded} timeout="auto" unmountOnExit>
            <Box sx={{ pb: 2, pt: 2, pl: isEditMode ? 11 : 0 }}>
              <TableContainer sx={{ backgroundColor: 'background.paper', borderRadius: 2 }}>
                <Table aria-label="endorsement-details">
                  <TableHead><TableRow><TableCell sx={{ width: '50%' }}>Name</TableCell><TableCell sx={{ width: '50%' }}>Title</TableCell></TableRow></TableHead>
                  <TableBody>{childRows.map((child) => renderChildRow(child))}</TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const Endorsements = forwardRef<
  EndorsementsHandle,
  {
    isEditMode?: boolean;
    policyEffectiveDate?: string | null;
    initialDraftDelta?: EndorsementsDraftDelta | null;
    onDraftDeltaChange?: (delta: EndorsementsDraftDelta) => void;
  }
>(function EndorsementsRenderer({ isEditMode = false, policyEffectiveDate, initialDraftDelta = null, onDraftDeltaChange }, ref) {
  const { comboId, coverageId, policyId } = useParams<{ comboId: string; coverageId: string; policyId: string }>();
  const { data } = useEndorsementsByPolicyId(Number(comboId), Number(coverageId), Number(policyId));
  const { data: typeData } = useEndorsementTypeLookups();
  const { data: descriptorData } = useEndorsementDescriptorTypeLookups();
  const typeOptions = useMemo(() => typeData?.data ?? [], [typeData?.data]);
  const descriptorOptions = useMemo(() => descriptorData?.data ?? [], [descriptorData?.data]);
  const initialRows = useMemo(
    () => (data?.data ?? []).map((item, index) => toDraft(item, index)),
    [data?.data]
  );
  const [draftRows, setDraftRows] = useState<DraftEndorsement[]>(initialRows);
  const [snapshot, setSnapshot] = useState<DraftEndorsement[] | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const initialDraftDeltaRef = useRef(initialDraftDelta);
  const hasAppliedInitialDeltaRef = useRef(false);
  // Tracks the last data?.data reference actually processed, so a mount-effect re-invocation with
  // unchanged data (e.g. Strict Mode's double-invoke) is a no-op instead of re-resetting the draft.
  const lastProcessedDataRef = useRef<unknown>(undefined);

  useEffect(() => {
    if (!data?.data || lastProcessedDataRef.current === data.data) {
      return;
    }
    lastProcessedDataRef.current = data.data;
    if (!hasAppliedInitialDeltaRef.current) {
      hasAppliedInitialDeltaRef.current = true;
      setDraftRows(applyEndorsementsDraftDelta(initialRows, initialDraftDeltaRef.current));
    } else {
      setDraftRows(initialRows);
    }
    setSnapshot(null);
    setErrors({});
  }, [initialRows, data?.data]);

  useEffect(() => { if (isEditMode) setSnapshot((current) => current ?? draftRows); else setSnapshot(null); }, [draftRows, isEditMode]);

  useEffect(() => {
    if (!hasAppliedInitialDeltaRef.current || !data?.data) {
      return;
    }
    onDraftDeltaChange?.(getEndorsementsDraftDelta(snapshot ?? initialRows, draftRows));
  }, [draftRows, snapshot, initialRows, onDraftDeltaChange, data?.data]);

  useImperativeHandle(ref, () => ({
    validateForSave: () => {
      const nextErrors = validateEndorsementRows(draftRows, typeOptions, policyEffectiveDate);
      setErrors(nextErrors);
      return Object.keys(nextErrors).length === 0;
    },
    hasPendingChanges: () => JSON.stringify(snapshot) !== JSON.stringify(draftRows),
    getChangedFieldSummaries: () => getEndorsementsChangedFieldSummaries(snapshot, draftRows, typeOptions, descriptorOptions),
    getEndorsementsPayload: () => toPayload(draftRows),
    commitEdit: () => setSnapshot(draftRows),
    cancelEdit: () => { if (snapshot) setDraftRows(snapshot); setErrors({}); },
  }), [descriptorOptions, draftRows, policyEffectiveDate, snapshot, typeOptions]);

  const update = (id: number, value: Partial<DraftEndorsement>) => setDraftRows((rows) => rows.map((row) => row.endorsementId === id ? { ...row, ...value } : row));
  const onEndorsementTypeChange = (id: number, option: EndorsementTypeLookupOption | null) => {
    const nextEndorsementCode = option?.endorsementCode ?? '';
    const currentRow = draftRows.find((row) => row.endorsementId === id);
    const patch: Partial<DraftEndorsement> = {
      endorsementTypeId: option?.endorsementTypeId ?? 0,
      endorsementCode: nextEndorsementCode,
    };

    if (!supportsWc000308(nextEndorsementCode)) {
      patch.wc000308s = [];
    } else if (!currentRow?.wc000308s.length) {
      patch.wc000308s = [{ endorsementWc000308Id: undefined, descriptorCode: '', excludedName: '' }];
    }

    if (!supportsWc000310(nextEndorsementCode)) {
      patch.wc000310s = [];
    } else if (!currentRow?.wc000310s.length) {
      patch.wc000310s = [{ endorsementWc000310Id: undefined, descriptorCode: '', includedName: '' }];
    }

    update(id, patch);
    setErrors((previous) => {
      const key = `${id}.endorsementTypeId`;
      const error = option
        ? getEndorsementTypeSelectionError(option.endorsementTypeId, typeOptions, policyEffectiveDate)
        : 'Select a valid endorsement type.';
      if (!error) {
        const next = { ...previous };
        delete next[key];
        return next;
      }
      return { ...previous, [key]: error };
    });
  };
  const rows = [...draftRows].sort((left, right) => {
    // Sort by the pre-edit snapshot so editing a row's date/type doesn't reorder it mid-edit.
    const leftSortKey = snapshot?.find((item) => item.endorsementId === left.endorsementId) ?? left;
    const rightSortKey = snapshot?.find((item) => item.endorsementId === right.endorsementId) ?? right;
    return rightSortKey.policyChangeEffectiveDate.localeCompare(leftSortKey.policyChangeEffectiveDate) || leftSortKey.endorsementCode.localeCompare(rightSortKey.endorsementCode);
  });

  return <Card sx={{ display: 'flex', zIndex: 'unset', overflow: 'unset', mt: 5, borderRadius: 2, boxShadow: 'var(--customShadows-card)', width: useHeaderWidth() }}><CardContent sx={{ flexGrow: 1, padding: '0px' }} data-testid="endorsements-card"><Box sx={{ p: 3 }}><Typography variant="h5" sx={{ color: 'text.primary' }} data-testid="endorsements-title">Endorsements</Typography><TableContainer sx={{ mt: 1.5, border: '1px solid var(--palette-divider)', borderRadius: 2 }}><Table sx={{ minWidth: 500 }} data-testid="endorsements-table"><TableHead><TableRow><TableCell /><TableCell>Endorsement</TableCell><TableCell>Effective date</TableCell><TableCell>Expiration date</TableCell><TableCell>Received date</TableCell><TableCell /></TableRow></TableHead><TableBody data-testid="endorsements-table-body">{rows.map((row, index) => <EndorsementRow key={row.endorsementId} row={row} rowIndex={index} isEditMode={isEditMode} typeOptions={typeOptions} descriptorOptions={descriptorOptions} errors={errors} update={update} onEndorsementTypeChange={onEndorsementTypeChange} />)}<TableNoData notFound={!rows.length} data-testid="endorsements-no-data" /></TableBody></Table></TableContainer></Box></CardContent></Card>;
});

Endorsements.displayName = 'Endorsements';

export default memo(Endorsements);